import React, { useState, useEffect } from "react";
import {
    Box,
    Container,
    Typography,
    Divider,
    TextField,
    Autocomplete,
    Chip,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import queryGraphql from "../../../helpers/ApiInterface";
import MainDrawer from "../../MainDrawer";
import ConsumerGroupRequestsTable from "./ConsumerGroupRequestsTable";
import MiniBot from "../../MiniBot";

interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: string;
    groupBy: string;
}

let searchOptions: SearchOptions[] = [];

const ConsumerGroupRequestsPage: React.FC = () => {
    const [consumerGroups, setConsumerGroups] = useState<any[]>([]);
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
    const [updates, setUpdates] = useState<any[]>([]);
    
    useEffect(() => {
        const fetchStatuses = async () => {
            try {
                const data = await queryGraphql(`
                    query Query {
                      resourceStatuses {
                        id
                        currentStatus
                        resourceId
                      }
                    }`);
                if (data.resourceStatuses) {
                    setUpdates(data.resourceStatuses);
                }
            } catch (e) {
                console.log(e);
            }
        };
        fetchStatuses();
    }, []);  
    
    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await queryGraphql(`
                    query GetConsumerGroups {
                      getConsumerGroups {
                        id
                        name
                        status
                        reasonForRejection
                        createdAt
                        updatedAt
                        consumerGroupOwner {
                          id
                          name
                          googleProfilePicture 
                        }
                        consumerGroupApprover {
                          id
                          name
                          googleProfilePicture 
                        }
                      }
                    }`);

                const rawConsumerGroupData = data.getConsumerGroups || [];

                let consumerGroupsArr = rawConsumerGroupData.map((cg: any) => ({
                    id: cg.id,
                    consumerGroupName: cg.name,
                    status: cg.status ? cg.status.toLowerCase() : 'pending',
                    createdAt: cg.createdAt ? new Date(Number(cg.createdAt)).toISOString() : null,
                    actionedAt: cg.updatedAt ? new Date(Number(cg.updatedAt)).toISOString() : null,
                    ownerName: cg.consumerGroupOwner?.name,
                    ownerAvatar: cg.consumerGroupOwner?.googleProfilePicture,
                    approverName: cg.consumerGroupApprover?.name,
                    approverAvatar: cg.consumerGroupApprover?.googleProfilePicture, 
                    reasonForRejection: cg.reasonForRejection,
                }));
                setConsumerGroups(consumerGroupsArr);

                searchOptions = consumerGroupsArr
                    .map((cg: any) => {
                        const options: SearchOptions[] = [
                            { category: "consumerGroupName", groupBy: "CONSUMER GROUP NAME", chipLabel: `Name: ${cg.consumerGroupName}`, dropdownLabel: cg.consumerGroupName },
                            { category: "ownerName", groupBy: "OWNER", chipLabel: `Owner: ${cg.ownerName}`, dropdownLabel: cg.ownerName },
                            { category: "status", groupBy: "STATUS", chipLabel: `Status: ${cg.status}`, dropdownLabel: cg.status },
                        ];
                        return options;
                    })
                    .flat();

                searchOptions = Array.from(new Map(searchOptions.map(item => [`${item.category}:${item.dropdownLabel}`, item])).values());
                searchOptions.sort((a, b) => a.groupBy.localeCompare(b.groupBy) || a.dropdownLabel.localeCompare(b.dropdownLabel));

            } catch (error) {
                console.log(error);
            }
        };

        fetchData();
    }, []);

    const filteredConsumerGroups =
        searchQuery.length === 0
            ? consumerGroups
            : consumerGroups.filter((cg: any) =>
                searchQuery.every(filter => {
                    const apiValue = cg[filter.category]?.toLowerCase();
                    return searchQuery.filter(f => f.category === filter.category).map(f => f.dropdownLabel.toLowerCase()).includes(apiValue);
                })
            );
    
    const filteredActiveCGs = filteredConsumerGroups.filter(cg => ["active", "approved"].includes(cg.status));
    const filteredPendingCGs = filteredConsumerGroups.filter(cg => cg.status === "pending");
    const filteredRejectedCGs = filteredConsumerGroups.filter(cg => cg.status === "rejected");

    return (
        <Box sx={{ display: "flex" }}>
            <MainDrawer index={"requests-consumer-group"} />
            <MiniBot/>
            <Box component="main" sx={{ flex: 1, bgcolor: "#fafafa", minHeight: window.innerHeight }}>
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={2} mb="18px">
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", my: 1, mx: 2 }}>
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">Requests</Typography>
                            <IconButton aria-label="Help" onClick={() => window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs`, "_blank")} sx={{ p: 0, border: 'none', mr:2 }}>
                                <Box component="img" src="/assets/Help.svg" alt="Help Icon" sx={{ width: 40, height: 40 }}/>
                            </IconButton>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mx: 2 }}>
                        <Box>
                            <Typography fontFamily="Gilroy-SemiBold" fontSize="16px">{`Consumer Groups (${consumerGroups.length})`}</Typography>
                            <Typography fontFamily="Gilroy-Regular" fontSize="14px">{`View all the requests raised for Consumer Groups`}</Typography>
                        </Box>
                        <Autocomplete
                            multiple
                            options={searchOptions}
                            filterSelectedOptions
                            getOptionLabel={(option) => option.dropdownLabel}
                            groupBy={(option) => option.groupBy}
                            renderTags={(tagValue, getTagProps) =>
                                tagValue.map((option, index) => <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} />)
                            }
                            onChange={(_, newVal) => setSearchQuery(newVal)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="filled"
                                    placeholder="Search by name, owner, status etc"
                                    fullWidth
                                    size="small"
                                    hiddenLabel
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <InputAdornment position="start">
                                                    <img src="/assets/search-normal.svg" alt="Search" style={{ width: "16px", paddingLeft: "4px" }}/>
                                                </InputAdornment>
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                        disableUnderline: true,
                                        style: {
                                            backgroundColor: "#f4f4f4",
                                            minHeight: "45px",
                                            width: "500px",
                                            borderRadius: "8px",
                                            fontFamily: "Gilroy-Regular",
                                            fontSize: "14px",
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                    </Box>
                    <Box m={2}>
                        <Accordion defaultExpanded={filteredPendingCGs.length > 0} elevation={0} sx={{ borderRadius: "8px", mb: 2, '&:before': { display: 'none' }, bgcolor: "#fafafa" }} disabled={filteredPendingCGs.length === 0}>
                            <AccordionSummary expandIcon={<ExpandMore />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/clock.svg" alt="Pending" style={{ width: "20px", marginRight: "8px" }} /><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Pending Consumer Groups ({filteredPendingCGs.length})</Typography></Box></AccordionSummary>
                            <AccordionDetails><ConsumerGroupRequestsTable consumerGroups={filteredPendingCGs} resourceStatuses={updates} /></AccordionDetails>
                        </Accordion>
                        <Accordion defaultExpanded={filteredActiveCGs.length > 0} elevation={0} sx={{ borderRadius: "8px", mb: 2, '&:before': { display: 'none' }, bgcolor: "#fafafa" }} disabled={filteredActiveCGs.length === 0}>
                            <AccordionSummary expandIcon={<ExpandMore />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/tickcircle.svg" alt="Active" style={{ width: "20px", marginRight: "8px" }} /><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Active Consumer Groups ({filteredActiveCGs.length})</Typography></Box></AccordionSummary>
                            <AccordionDetails><ConsumerGroupRequestsTable consumerGroups={filteredActiveCGs} resourceStatuses={updates} /></AccordionDetails>
                        </Accordion>
                        <Accordion defaultExpanded={filteredRejectedCGs.length > 0} elevation={0} sx={{ borderRadius: "8px", '&:before': { display: 'none' }, bgcolor: "#fafafa" }} disabled={filteredRejectedCGs.length === 0}>
                            <AccordionSummary expandIcon={<ExpandMore />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/reject.svg" alt="Rejected" style={{ width: "20px", marginRight: "8px" }} /><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Rejected Consumer Groups ({filteredRejectedCGs.length})</Typography></Box></AccordionSummary>
                            <AccordionDetails><ConsumerGroupRequestsTable consumerGroups={filteredRejectedCGs} resourceStatuses={updates} /></AccordionDetails>
                        </Accordion>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default ConsumerGroupRequestsPage;